import React from 'react'
import PropTypes from 'prop-types'
import { Col, Row } from 'reactstrap'
import GoogleMapsOverlay from './GoogleMapsOverlay'

const formatDistance = distance => {
  const normalizedDistance =
    typeof distance === 'number' ? distance : Number.parseFloat(distance)

  if (Number.isFinite(normalizedDistance)) {
    return `${normalizedDistance.toFixed(1)} mi`
  }

  return '0.0 mi'
}

const formatPhoneNumber = phoneNumber => {
  const digitsOnly = String(phoneNumber || '').replace(/\D/g, '')

  if (digitsOnly.length < 10) {
    return null
  }

  const normalizedDigits = digitsOnly.slice(-10)
  const areaCode = normalizedDigits.substring(0, 3)
  const exchange = normalizedDigits.substring(3, 6)
  const lineNumber = normalizedDigits.substring(6, 10)

  return {
    displayPhoneNumber: `(${areaCode}) ${exchange}-${lineNumber}`,
    hrefPhoneNumber: normalizedDigits,
  }
}

const BasicStoreInfo = ({
  selectedStore,
  storeSearchResults,
  hideNearestStoreCopy,
}) => {
  if (!selectedStore) {
    return (
      <React.Fragment>
        {!hideNearestStoreCopy && (
          <p data-test="text.nearest-store-copy">
            Based on your previously entered zip code, this is the nearest store
            location.
          </p>
        )}

        <div className="pb-3" data-test="nearest-store-container">
          <Row className="align-items-stretch">
            <Col xs={12} md={5} className="mb-3 mb-md-0">
              <div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
              </div>
            </Col>

            <Col xs={12} md={7} className="d-none d-lg-flex">
              <div className="w-100 h-100">
                <GoogleMapsOverlay
                  selectedStore={selectedStore}
                  storeSearchResults={storeSearchResults}
                  minHeight={0}
                  ignoreOverlay={true}
                />
              </div>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    )
  }

  const distance = formatDistance(selectedStore.distance)
  const formattedPhone =
    hideNearestStoreCopy && selectedStore.phone
      ? formatPhoneNumber(selectedStore.phone)
      : null

  return (
    <React.Fragment>
      {!hideNearestStoreCopy && (
        <p data-test="text.nearest-store-copy">
          Based on your previously entered zip code, this is the nearest store
          location:
        </p>
      )}

      <div className="pb-3" data-test="nearest-store-container">
        <Row className="align-items-stretch">
          <Col xs={12} md={5} className="mb-3 mb-md-0">
            <div>
              <div className="font-weight-bold">{selectedStore.storeName}</div>
              <div>{selectedStore.addressLine1}</div>
              {!!selectedStore.addressLine2 && <div>{selectedStore.addressLine2}</div>}
              <div>
                {selectedStore.city}, {selectedStore.state} {selectedStore.postalCode}
              </div>
            </div>
          </Col>

          <Col xs={12} md={7} className="d-none d-lg-flex">
            <div className="w-100 h-100">
              <GoogleMapsOverlay
                selectedStore={selectedStore}
                storeSearchResults={storeSearchResults}
                minHeight={0}
                ignoreOverlay={true}
              />
            </div>
          </Col>
        </Row>

        {(!!formattedPhone || !hideNearestStoreCopy) && (
          <Row>
            <Col xs={12} md={5} className="pt-2">
              {!!formattedPhone && (
                <div data-test="text.store-phone">
                  <a href={`tel:${formattedPhone.hrefPhoneNumber}`}>
                    {formattedPhone.displayPhoneNumber}
                  </a>
                </div>
              )}

              {!hideNearestStoreCopy && (
                <div className={formattedPhone ? 'mt-1' : ''} data-test="text.store-distance" style={{ color: '#0B6637' }}>
                  <i className="fas fa-map-marker-alt" style={{ color: '#E95101' }} /> Distance: {distance}
                </div>
              )}
            </Col>
          </Row>
        )}
      </div>
    </React.Fragment>
  )
}

BasicStoreInfo.propTypes = {
  selectedStore: PropTypes.shape({
    storeName: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
    distance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  storeSearchResults: PropTypes.array,
  hideNearestStoreCopy: PropTypes.bool,
}

export { formatDistance, formatPhoneNumber }
export default BasicStoreInfo
